/**
 * Investor-facing budget vs actual, split by project phase.
 *
 * Reading the numbers phase by phase is what makes them meaningful: the Development &
 * Renovation budget is what produced the appreciation or sale, and the Operation budget is what
 * produced the rental NOI. Phases follow the strategy, so a flip shows no Operation and a
 * residential rental shows no Exit.
 */
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { api, useAuth } from '../../context/AuthContext';
import {
  Clock, ArrowUpRight, ArrowDownRight, ArrowRight, Minus, X, ListTree,
} from 'lucide-react';
import { phaseMeta, UNASSIGNED_PHASE } from '../../lib/projectPhases';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const metaFor = (phase) => (phase === UNASSIGNED_PHASE.value ? UNASSIGNED_PHASE : phaseMeta(phase));

// Revenue over plan is good; cost over plan is not
const Variance = ({ budget, actual, invert = false }) => {
  const diff = (actual || 0) - (budget || 0);
  const pct = budget ? (diff / Math.abs(budget)) * 100 : null;
  if (!diff) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-foreground-muted">
        <Minus className="h-3 w-3" /> On budget
      </span>
    );
  }
  const good = invert ? diff > 0 : diff < 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      {diff > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {formatCurrency(Math.abs(diff))}
      {pct !== null && ` (${diff > 0 ? '+' : '-'}${Math.abs(pct).toFixed(1)}%)`}
    </span>
  );
};

const PhaseTable = ({ phases }) => (
  <div data-testid="investor-phase-table">
    <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-x-3 md:gap-x-5 pb-2.5 border-b border-black/[0.08]">
      <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">Phase</span>
      <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted text-right">Budget</span>
      <span className="text-[11px] uppercase tracking-[0.14em] text-primary font-medium text-right">Actual</span>
      <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted text-right">Variance</span>
    </div>
    <div className="divide-y divide-black/[0.06]">
      {phases.map((phase) => {
        const meta = metaFor(phase.phase);
        const PhaseIcon = meta.icon;
        // Earning phases are judged on what they netted; spending phases on whether they held budget
        const earning = phase.has_income;
        const headline = earning ? phase.net : phase.costs;
        return (
          <div
            key={phase.phase}
            className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-x-3 md:gap-x-5 py-3.5 items-baseline"
            data-testid={`investor-phase-${phase.phase}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <PhaseIcon className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm truncate" title={meta.label}>{meta.label}</span>
            </div>
            <span className="text-sm text-right text-foreground-muted tabular-nums">
              {formatCurrency(headline?.budget)}
            </span>
            <span className="text-sm text-right font-medium tabular-nums">{formatCurrency(headline?.actual)}</span>
            <span className="text-right">
              {phase.entries_count > 0 ? (
                <Variance budget={headline?.budget} actual={headline?.actual} invert={earning} />
              ) : (
                <span className="text-sm text-foreground-muted">Not started</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const DetailSheet = ({ data, onClose }) => (
  <div className="fixed inset-0 z-[95] bg-[rgb(var(--background))] overflow-y-auto" data-testid="phase-financials-detail-sheet">
    <div className="sticky top-0 z-10 bg-[rgb(var(--background))]/95 backdrop-blur-md border-b border-black/[0.06]">
      <div className="container mx-auto px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-muted">Financials</p>
          <h2 className="text-lg md:text-xl font-heading font-semibold">Budget vs actual, in detail</h2>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/[0.05]" data-testid="phase-financials-sheet-close">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>

    <div className="container mx-auto px-6 md:px-12 lg:px-20 py-8 space-y-12 max-w-5xl">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ['Total costs', formatCurrency(data.totals?.costs)],
          ['Total income', formatCurrency(data.totals?.income)],
          ['Net result', formatCurrency(data.totals?.net)],
          ['Phases tracked', String(data.phases?.length || 0)],
        ].map(([label, value]) => (
          <div key={label} className="p-3 bg-black/[0.03] rounded-lg">
            <div className="text-[10px] uppercase tracking-wide text-foreground-muted mb-1">{label}</div>
            <div className="text-base sm:text-lg font-bold">{value}</div>
          </div>
        ))}
      </div>

      {data.phases?.map((phase) => {
        const meta = metaFor(phase.phase);
        const PhaseIcon = meta.icon;
        return (
          <section key={phase.phase} data-testid={`phase-detail-${phase.phase}`}>
            <div className="flex items-center gap-2 mb-1">
              <PhaseIcon className="h-4 w-4 text-primary" />
              <h3 className="text-base font-heading font-semibold">{meta.label}</h3>
            </div>
            <p className="text-xs text-foreground-muted mb-4">{meta.description}</p>

            <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-black/[0.03] rounded-t-lg">
              <div>Account</div>
              <div className="text-right">Budget</div>
              <div className="text-right">Actual</div>
              <div className="text-right">Variance</div>
            </div>

            {phase.categories?.length === 0 ? (
              <p className="text-xs text-foreground-muted px-3 py-4">Nothing recorded yet.</p>
            ) : (
              phase.categories.map((cat) => (
                <div key={cat.category}>
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-foreground-muted bg-black/[0.02]">
                    {cat.category}
                  </div>
                  {cat.accounts?.map((acc) => (
                    <div key={acc.account_code} className="grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-black/[0.05]">
                      <div className="truncate" title={acc.account_name}>{acc.account_name}</div>
                      <div className="text-right text-foreground-muted">{formatCurrency(acc.budget)}</div>
                      <div className="text-right">{formatCurrency(acc.actual)}</div>
                      <div className="text-right">
                        <Variance budget={acc.budget} actual={acc.actual} invert={acc.account_type === 'revenue'} />
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}

            <div className="grid grid-cols-4 gap-2 text-xs px-3 py-3 bg-primary/10 rounded-b-lg font-bold">
              <div>{phase.has_income ? `Net ${meta.shortLabel}` : `Total ${meta.shortLabel}`}</div>
              <div className="text-right">
                {formatCurrency(phase.has_income ? phase.net?.budget : phase.costs?.budget)}
              </div>
              <div className="text-right">
                {formatCurrency(phase.has_income ? phase.net?.actual : phase.costs?.actual)}
              </div>
              <div className="text-right">
                <Variance
                  budget={phase.has_income ? phase.net?.budget : phase.costs?.budget}
                  actual={phase.has_income ? phase.net?.actual : phase.costs?.actual}
                  invert={phase.has_income}
                />
              </div>
            </div>

          </section>
        );
      })}

      <p className="text-xs text-foreground-muted italic">
        Figures are reported per phase. Actuals include approved and posted movements only.
      </p>
    </div>
  </div>
);

const PhaseFinancialsSection = ({ projectId, emptyMessage }) => {
  const { setShowAuthModal } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const openAuth = () => setShowAuthModal(true);

  useEffect(() => {
    let active = true;
    api.get(`/projects/${projectId}/phase-financials`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId]);

  const phases = data?.phases || [];
  const canShow = !loading && data?.is_logged_in && phases.length > 0;

  if (!loading && data && !data.is_logged_in) {
    return (
      <Card
        className="bg-white rounded-2xl border border-black/[0.05] px-5 py-5 md:px-7 md:py-6"
        data-testid="phase-financials-locked"
      >
        <div className="flex items-center gap-2">
          <ListTree className="h-[17px] w-[17px] text-primary" />
          <h3 className="text-sm md:text-base font-heading">Detailed financial data</h3>
        </div>

        <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-black/[0.06]">
          <p className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">
            Members only
          </p>
          <p className="text-xs md:text-sm text-foreground-muted leading-relaxed mt-1.5 md:max-w-3xl">
            Create a free account or log in to see the full financial data for this project: budget against actual for
            every phase, the accounts behind each figure and the net result to date.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
            <button
              onClick={openAuth}
              className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-foreground-muted hover:text-slate-900 transition-colors"
              data-testid="phase-financials-login-btn"
            >
              Log in
            </button>
            <button
              onClick={openAuth}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-white text-sm font-medium px-5 py-2.5 hover:opacity-90 active:opacity-80 transition-opacity"
              data-testid="phase-financials-create-account-btn"
            >
              Create free account
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </Card>
    );
  }

  if (!canShow) {
    // Used when this is the only financial data the project has
    if (!loading && emptyMessage) {
      return (
        <Card className="bg-white rounded-2xl border border-black/[0.05] p-6">
          <p className="text-foreground-muted text-center">{emptyMessage}</p>
        </Card>
      );
    }
    return null;
  }

  return (
    <Card className="bg-white rounded-2xl border border-black/[0.05] p-6" data-testid="investor-phase-financials">
      <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-1 flex items-center gap-2">
        <ListTree className="h-5 w-5 text-primary" />
        Performance by Phase
      </h3>
      <p className="text-xs text-foreground-muted mb-6">
        Budget against actual for each stage this project goes through
      </p>

      {!data.has_data ? (
        <div className="py-8 text-center">
          <Clock className="h-10 w-10 mx-auto text-foreground-muted mb-3 opacity-50" />
          <p className="text-sm text-foreground-muted">
            Phase budgets and actuals will appear here as the project progresses.
          </p>
        </div>
      ) : (
        <>
          <PhaseTable phases={phases} />

          <div className="flex items-center justify-between gap-4 mt-4 pt-3 border-t border-black/[0.08]">
            <span className="text-sm font-semibold">Net result</span>
            <span className="text-sm">
              <span className="text-foreground-muted">{formatCurrency(data.totals?.net_budget)} planned</span>
              {' · '}
              <span className="font-semibold">{formatCurrency(data.totals?.net)} to date</span>
            </span>
          </div>

          <div className="mt-6 pt-5 border-t border-black/[0.06] md:flex md:items-start md:justify-between md:gap-10">
            <p className="text-xs md:text-sm text-foreground-muted leading-relaxed md:max-w-2xl">
              Every account, phase by phase, with the budget it was given and what it has actually spent or earned.
            </p>
            <button
              onClick={() => setSheetOpen(true)}
              className="mt-3 md:mt-0 inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-primary hover:opacity-70 active:opacity-60 transition-opacity md:flex-shrink-0"
              data-testid="view-phase-financials-details-btn"
            >
              View detailed breakdown
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      )}

      {sheetOpen && <DetailSheet data={data} onClose={() => setSheetOpen(false)} />}
    </Card>
  );
};

export default PhaseFinancialsSection;
